import React from 'react'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import CheckIcon from '@mui/icons-material/Check'

// The applicant wizard's step list: a progress summary, then each step with
// a marker showing done (tick), still-to-do (count) or the review step.
const StepMarker = ({ index, isSelected, isComplete, isReview }) => {
  const done = isComplete && !isReview
  return (
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.8rem',
        fontWeight: 700,
        border: '2px solid',
        borderColor: done
          ? 'success.main'
          : isSelected
            ? 'primary.main'
            : 'divider',
        bgcolor: done ? 'success.main' : isSelected ? 'primary.main' : 'transparent',
        color: done || isSelected ? '#fff' : 'text.secondary',
      }}
    >
      {done ? <CheckIcon sx={{ fontSize: 16 }} /> : index + 1}
    </Box>
  )
}

const StepNav = ({
  steps,
  selected,
  outstandingByStep,
  completeCount,
  totalCount,
  onSelect,
}) => (
  <Stack gap={2}>
    <Box>
      <Stack direction="row" justifyContent="space-between" mb={0.75}>
        <Typography variant="body2" fontWeight={600} color="text.primary">
          Your progress
        </Typography>
        <Typography variant="body2">
          {completeCount} of {totalCount}
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        color={completeCount === totalCount ? 'success' : 'primary'}
        value={(completeCount / totalCount) * 100}
        sx={{ height: 6, borderRadius: 3 }}
      />
    </Box>
    <Stack component="nav" aria-label="Application steps" gap={0.25}>
      {steps.map((step, index) => {
        const isSelected = step.key === selected
        const isReview = step.key === 'review'
        const outstanding = outstandingByStep[step.key] ?? 0
        return (
          <ButtonBase
            key={step.key}
            onClick={() => onSelect(step.key)}
            aria-current={isSelected ? 'step' : undefined}
            sx={{
              justifyContent: 'flex-start',
              textAlign: 'left',
              borderRadius: 1,
              px: 1,
              py: 0.9,
              gap: 1.5,
              bgcolor: isSelected ? 'anMuted.main' : 'transparent',
              '&:hover': { bgcolor: 'anMuted.main' },
            }}
          >
            <StepMarker
              index={index}
              isSelected={isSelected}
              isComplete={!outstanding}
              isReview={isReview}
            />
            <Box minWidth={0} flex={1}>
              <Typography
                variant="body2"
                fontWeight={isSelected ? 700 : 500}
                color="text.primary"
                noWrap
              >
                {step.title}
              </Typography>
              {!isReview && outstanding > 0 && (
                <Typography variant="caption" color="warning.dark">
                  {outstanding} to complete
                </Typography>
              )}
            </Box>
          </ButtonBase>
        )
      })}
    </Stack>
  </Stack>
)

export default StepNav
